export const APP_LOCALE = 'en-GB';

export const DATE_PRESETS = {
  time: { hour: '2-digit', minute: '2-digit' },
  dayMonth: { day: 'numeric', month: 'short' },
  date: { day: '2-digit', month: '2-digit', year: 'numeric' },
  longDate: { day: 'numeric', month: 'long', year: 'numeric' },
  dateTime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export type DatePreset = keyof typeof DATE_PRESETS;

export type DateInput = Date | string | number | null | undefined;

export function toValidDate(value: DateInput): Date | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function formatDate(value: DateInput, preset: DatePreset, fallback = ''): string {
  const date = toValidDate(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat(APP_LOCALE, DATE_PRESETS[preset]).format(date);
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function feedTimePreset(date: Date, now = new Date()): DatePreset {
  return isSameDay(date, now) ? 'time' : 'dayMonth';
}

export function localCalendarDate(year: number, month: number, day: number): string | undefined {
  const date = new Date(year, month - 1, day);
  const isRealDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isRealDate ? toDateInputValue(date) : undefined;
}

// A date input shows a local calendar date, so this reads local fields rather than slicing the UTC ISO string.
export function toDateInputValue(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export type MoneyInput = number | string | null | undefined;

export interface MoneyOptions {
  wholeUnits?: boolean;
}

export function isCurrencyCode(currency: string | null | undefined): currency is string {
  if (!currency) return false;
  try {
    new Intl.NumberFormat(APP_LOCALE, { style: 'currency', currency });
    return true;
  } catch {
    return false;
  }
}

// An unknown currency still shows the figure, with whatever unit was recorded after it.
export function formatMoney(
  amount: MoneyInput,
  currency: string | null | undefined,
  { wholeUnits = false }: MoneyOptions = {},
) {
  const value = Number(amount ?? 0);
  const digits = wholeUnits ? { maximumFractionDigits: 0 } : {};
  if (isCurrencyCode(currency)) {
    return new Intl.NumberFormat(APP_LOCALE, { style: 'currency', currency, ...digits }).format(value);
  }
  const plain = new Intl.NumberFormat(APP_LOCALE, digits).format(value);
  return currency ? `${plain} (${currency})` : plain;
}
